// Menu « ⋮ » des tableaux compacts (cartes Rendez-vous et Patients du tableau de bord, page Rendez-vous) : un petit
// bouton qui ouvre la liste des actions de la ligne.
// - accessibilité : bouton déclencheur (libellé, aria-haspopup, aria-expanded), liste role="menu", entrées
//   role="menuitem" ;
// - clavier : Entrée, Espace ou ↓ ouvrent sur la première entrée, ↑ sur la dernière ; ↓ ↑ Début Fin parcourent les
//   entrées (en boucle) ; Échap ferme et rend le focus au bouton ; Tab ferme ;
// - un clic hors du menu, un défilement ou un redimensionnement le ferment ;
// - position fixe calculée à l'ouverture : le menu n'est pas coupé par le défilement horizontal du tableau et s'ouvre
//   vers le haut en bas d'écran.
// actions : [{ id, libelle, danger?, disabled?, aide? }] ; le choix ferme le menu puis émet « emitAction » avec l'id.

export const HAUTEUR_ENTREE = 36

export default {
    name: 'menu-actions',
    props: {
        actions: { type: Array, default: () => [] },
        libelle: { type: String, default: 'Actions' },   // libellé du bouton (info-bulle et lecteurs d'écran)
        disabled: { type: Boolean, default: false }
    },
    data() {
        return { ouvert: false, position: null }
    },

    watch: {
        disabled(valeur) {
            if (valeur) this.fermer(false)
        },

        actions_visibles(liste) {
            if (!liste.length) this.fermer(false)
        }
    },

    computed: {
        actions_visibles() {
            return (Array.isArray(this.actions) ? this.actions : []).filter(action => action && action.id && action.libelle)
        },

        id_declencheur() {
            return 'menu-actions-' + this._uid
        },

        id_menu() {
            return 'menu-actions-' + this._uid + '-liste'
        }
    },

    beforeDestroy() {
        this.retirer_ecouteurs()
    },

    methods: {
        basculer() {
            if (this.ouvert) this.fermer(true)
            else this.ouvrir('premier')
        },

        // cible : 'premier', 'dernier' ou l'indice de l'entrée à focaliser
        ouvrir(cible) {
            if (this.ouvert || this.disabled || !this.actions_visibles.length) return
            this.position = this.calculer_position()
            this.ouvert = true
            this.ajouter_ecouteurs()
            this.$nextTick(() => {
                if (this.ouvert) this.focaliser(cible)
            })
        },

        fermer(rendre_focus) {
            if (!this.ouvert) return
            this.ouvert = false
            this.retirer_ecouteurs()
            const bouton = this.$refs.declencheur
            if (rendre_focus && bouton && typeof bouton.focus === 'function') bouton.focus()
        },

        fermer_sans_focus() {
            this.fermer(false)
        },

        choisir(action) {
            if (!action || action.disabled) return
            this.fermer(true)
            this.$emit('emitAction', action.id)
        },

        // Entrées actives, dans l'ordre du menu
        entrees() {
            const liste = this.$refs.entree
            return (Array.isArray(liste) ? liste : (liste ? [liste] : [])).filter(bouton => bouton && !bouton.disabled)
        },

        focaliser(cible) {
            const entrees = this.entrees()
            if (!entrees.length) return
            const index = cible === 'dernier' ? entrees.length - 1 : (typeof cible === 'number' ? cible : 0)
            const bouton = entrees[((index % entrees.length) + entrees.length) % entrees.length]
            try {
                bouton.focus({ preventScroll: true })
            } catch (erreur) {
                bouton.focus()
            }
        },

        clavier(event) {
            const cle = event && event.key ? event.key : ''
            const haut = cle === 'ArrowUp' || cle === 'Up', bas = cle === 'ArrowDown' || cle === 'Down'
            if (!this.ouvert) {
                if ((haut || bas) && event.target === this.$refs.declencheur) {
                    event.preventDefault()
                    this.ouvrir(haut ? 'dernier' : 'premier')
                }
                return
            }
            if (cle === 'Escape' || cle === 'Esc') {
                event.preventDefault()
                event.stopPropagation()
                this.fermer(true)
                return
            }
            if (cle === 'Tab') {
                this.fermer(false)
                return
            }
            const entrees = this.entrees()
            const actuel = entrees.indexOf(typeof document !== 'undefined' ? document.activeElement : null)
            let suivant = null
            if (bas) suivant = actuel + 1
            else if (haut) suivant = actuel === -1 ? 'dernier' : actuel - 1
            else if (cle === 'Home') suivant = 0
            else if (cle === 'End') suivant = 'dernier'
            if (suivant === null) return
            event.preventDefault()
            this.focaliser(suivant)
        },

        clic_exterieur(event) {
            const cible = event ? event.target : null
            if (this.$el && cible && typeof this.$el.contains === 'function' && this.$el.contains(cible)) return
            this.fermer(false)
        },

        ajouter_ecouteurs() {
            if (typeof document !== 'undefined') {
                document.addEventListener('mousedown', this.clic_exterieur, true)
                document.addEventListener('touchstart', this.clic_exterieur, true)
                document.addEventListener('scroll', this.fermer_sans_focus, true)
            }
            if (typeof window !== 'undefined') window.addEventListener('resize', this.fermer_sans_focus)
        },

        retirer_ecouteurs() {
            if (typeof document !== 'undefined') {
                document.removeEventListener('mousedown', this.clic_exterieur, true)
                document.removeEventListener('touchstart', this.clic_exterieur, true)
                document.removeEventListener('scroll', this.fermer_sans_focus, true)
            }
            if (typeof window !== 'undefined') window.removeEventListener('resize', this.fermer_sans_focus)
        },

        // Sous le bouton, aligné à droite ; au-dessus quand la place manque en bas de l'écran. Les deux bords verticaux
        // sont toujours écrits (l'autre à « auto ») : sinon le « top: 100% » de repli de la feuille resterait actif à
        // l'ouverture vers le haut et repousserait la liste sous le bas de l'écran.
        calculer_position() {
            const bouton = this.$refs.declencheur
            if (typeof window === 'undefined' || !bouton || typeof bouton.getBoundingClientRect !== 'function') return null
            const rect = bouton.getBoundingClientRect()
            const racine = typeof document !== 'undefined' && document.documentElement ? document.documentElement : null
            const largeur = racine && racine.clientWidth ? racine.clientWidth : window.innerWidth
            const hauteur = racine && racine.clientHeight ? racine.clientHeight : window.innerHeight
            const menu = this.actions_visibles.length * HAUTEUR_ENTREE + 14
            const style = { position: 'fixed', top: 'auto', bottom: 'auto', right: Math.max(8, Math.round(largeur - rect.right)) + 'px' }
            if (hauteur - rect.bottom < menu + 8 && rect.top > menu + 8) style.bottom = Math.round(hauteur - rect.top + 4) + 'px'
            else style.top = Math.round(rect.bottom + 4) + 'px'
            return style
        }
    }
}
